// personalised-cart-count.js
// Custom cart count interceptor for personalized pack bundles
// This file modifies the cart count behavior to count entire personalized packs as a single item

(function() {
  // Initialize the interceptor when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    initializeCartCountInterceptor();
    setupFocusTrapSafety();
    
    // Safety timeout to ensure cart count becomes visible even if our script fails
    setTimeout(function() {
      if (document.documentElement.classList.contains('js-cart-loading')) {
        document.documentElement.classList.remove('js-cart-loading');
        console.warn('[PersonalisedCartCount] Safety timeout triggered to reveal cart count');
      }
    }, 3000); // 3 second fallback
  });

  async function initializeCartCountInterceptor() {
    // Only initialize if FoxTheme pubsub is available
    if (!window.FoxTheme || !window.FoxTheme.pubsub) {
      console.warn('[PersonalisedCartCount] FoxTheme pubsub not available');
      return;
    }

    // Subscribe to cart update events with high priority
    const unsubscribe = window.FoxTheme.pubsub.subscribe(
      window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      interceptCartUpdate
    );

    // Add immediate correction for initial page load
    try {
      // Fetch current cart data
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Calculate custom count
      const customCount = calculateCustomCartCount(cart);
      
      // Update all cart-count elements directly
      updateCartCountElements(customCount, cart.item_count === 0);
      
      // Remove loading class to reveal the updated cart count
      document.documentElement.classList.remove('js-cart-loading');
      
      console.debug('[PersonalisedCartCount] Corrected initial cart count:', {
        originalCount: cart.item_count,
        customCount
      });
    } catch (error) {
      console.error('[PersonalisedCartCount] Failed to correct initial cart count:', error);
    }

    // Log successful initialization
    console.debug('[PersonalisedCartCount] Initialized cart count interceptor');
  }
  
  // Setup safety wrapper for focus trap function to handle skipFocusHandling flag
  function setupFocusTrapSafety() {
    // Only proceed if FoxTheme a11y is available
    if (!window.FoxTheme || !window.FoxTheme.a11y || !window.FoxTheme.a11y.trapFocus) {
      console.warn('[PersonalisedCartCount] FoxTheme.a11y.trapFocus not available');
      return;
    }
    
    // Store original trapFocus function
    const originalTrapFocus = window.FoxTheme.a11y.trapFocus;
    
    // Replace with our safe version
    window.FoxTheme.a11y.trapFocus = function(container, elementToFocus = container) {
      // Get the last cart update event from pubsub subscribers
      const lastCartEvent = window.lastCartUpdateEvent || {};
      
      // Skip focus trapping if skipFocusHandling flag is present
      if (lastCartEvent.skipFocusHandling) {
        console.debug('[PersonalisedCartCount] Skipping focus trap due to skipFocusHandling flag');
        return;
      }
      
      // Add safety check for null elements
      if (!container || !elementToFocus) {
        console.debug('[PersonalisedCartCount] Skipping focus trap due to null elements');
        return;
      }
      
      // Proceed with original function
      try {
        originalTrapFocus(container, elementToFocus);
      } catch (error) {
        console.warn('[PersonalisedCartCount] Error in trapFocus:', error);
      }
    };
  }

  function interceptCartUpdate(event) {
    // Skip processing if there's no valid cart data
    if (!event || !event.cart) return;

    // Calculate the personalised bundle-aware count
    const customCount = calculateCustomCartCount(event.cart);

    // Directly update all <cart-count> elements in the DOM.
    // This runs after CartCount.onCartUpdate (which wrote the raw item_count)
    // but within the same synchronous JS tick, so there is no visible flicker.
    updateCartCountElements(customCount, customCount === 0);

    // Ensure the loading class is removed
    document.documentElement.classList.remove('js-cart-loading');

    // Store event reference for focus trap safety
    window.lastCartUpdateEvent = event;
  }

  function calculateCustomCartCount(cart) {
    // Track unique bundle identifiers
    const uniqueBundles = new Set();
    let nonBundleItemCount = 0;

    // Process each item in the cart
    cart.items.forEach(item => {
      if (item.properties && item.properties._rc_bundle_parent === 'personalised-pack') {
        // For bundle items, add the bundle ID to our set
        uniqueBundles.add(item.properties._rc_bundle);
      } else {
        // For non-bundle items, count normally (item × quantity)
        nonBundleItemCount += item.quantity;
      }
    });

    // Return the count of unique bundles plus the count of non-bundle items
    return uniqueBundles.size + nonBundleItemCount;
  }

  // Helper functions for debugging
  function getBundleItemsCount(cart) {
    return cart.items.filter(item => 
      item.properties && 
      item.properties._rc_bundle_parent === 'personalised-pack'
    ).reduce((sum, item) => sum + item.quantity, 0);
  }

  function getNonBundleItemsCount(cart) {
    return cart.items.filter(item => 
      !item.properties || 
      item.properties._rc_bundle_parent !== 'personalised-pack'
    ).reduce((sum, item) => sum + item.quantity, 0);
  }

  function getUniqueBundleCount(cart) {
    const bundleIds = new Set();
    cart.items.forEach(item => {
      if (item.properties && item.properties._rc_bundle_parent === 'personalised-pack') {
        bundleIds.add(item.properties._rc_bundle);
      }
    });
    return bundleIds.size;
  }
  
  function updateCartCountElements(count, isEmpty) {
    // Update all cart-count elements in the DOM
    document.querySelectorAll('cart-count').forEach(element => {
      if (count > 99) {
        element.innerHTML = `<span class="text-sm">99+</span>`;
        element.classList.add('cart-count--small-medium');
      } else {
        element.innerText = count;
        element.classList.remove('cart-count--small-medium');
      }
      
      // Handle visibility
      element.hidden = isEmpty;
    });
    
    // Update the cart-has-items class on document
    const method = isEmpty ? 'remove' : 'add';
    document.documentElement.classList[method]('cart-has-items');
  }
})();
